/**
 * @module ErrorTypes
 * @description This module provides classes for specific error types.
 */

export class AuthenticationError extends Error {
  constructor(message: string = 'Not authenticated') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class GraphqlError extends Error {
  constructor(
    public message: string,
    public code: number,
  ) {
    super(message);
    this.code = code;
  }
}

/** GraphQL numeric code for the backend order `AuthorizationError` */
export const GRAPHQL_AUTHORIZATION_ERROR_CODE = 40300100;

export enum InvitationErrorType {
  ALREADY_INVITED = 'ALREADY_INVITED',
  ALREADY_MEMBER = 'ALREADY_MEMBER',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface InvitationErrorDetails {
  email: string;
  type: InvitationErrorType;
  message: string;
}

export class InvitationsError extends Error {
  public readonly errors: InvitationErrorDetails[];
  public readonly successfulInvitations: string[];

  constructor(
    errors: InvitationErrorDetails[],
    successfulInvitations: string[] = [],
  ) {
    const message = `Failed to send ${errors.length} invitation(s)`;
    super(message);
    this.name = 'InvitationsError';
    this.errors = errors;
    this.successfulInvitations = successfulInvitations;
  }

  static getErrorMessage(
    errorType: InvitationErrorType,
    email: string,
  ): string {
    switch (errorType) {
      case InvitationErrorType.ALREADY_INVITED:
        return `${email} is already invited, please have them accept their pending invitation.`;
      case InvitationErrorType.ALREADY_MEMBER:
        return `${email} already accepted the invite, please have them login or request a password reset.`;
      case InvitationErrorType.UNKNOWN_ERROR:
      default:
        return `${email} could not be invited due to an unknown error.`;
    }
  }
}

export class GraphqlClientRequiredError extends Error {
  constructor() {
    super('Graphql client is required when using the Graphql backend');
  }
}
